import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useConfigByGrupo, useUpdateConfig, type ConfigItem } from "@/features/config/api";
import type { ApiError } from "@/lib/api";
import { setTz } from "@/lib/tz";

const FIELDS: { key: string; label: string; multiline?: boolean; placeholder?: string }[] = [
  { key: "isp.nombre_legal", label: "Razón social (nombre legal)", placeholder: "Tu ISP S.A. de C.V." },
  { key: "isp.nombre_comercial", label: "Nombre comercial", placeholder: "Tu ISP" },
  { key: "isp.rfc", label: "RFC", placeholder: "XAXX010101000" },
  { key: "isp.domicilio_fiscal", label: "Domicilio fiscal", multiline: true, placeholder: "Calle, número, colonia, CP, ciudad, estado" },
  { key: "isp.telefono", label: "Teléfono" },
  { key: "isp.email", label: "Email de contacto", placeholder: "soporte@tuisp.mx" },
  { key: "isp.web", label: "Sitio web", placeholder: "https://tuisp.mx" },
  { key: "isp.representante_legal", label: "Representante legal (firma contratos)" },
  { key: "isp.logo_url", label: "URL del logo (opcional)", placeholder: "https://.../logo.png" },
];

// rc130 — zonas IANA usuales en México (el campo acepta cualquier zona válida;
// el backend la valida al guardar).
const TZ_SUGERIDAS = [
  "America/Mexico_City", "America/Monterrey", "America/Merida", "America/Cancun",
  "America/Chihuahua", "America/Ciudad_Juarez", "America/Mazatlan", "America/Hermosillo",
  "America/Tijuana", "America/Bahia_Banderas", "America/Matamoros", "America/Ojinaga",
];
const K_TZ = "sistema.timezone";

export function DatosIspSection() {
  const { data: items, isLoading } = useConfigByGrupo("isp");
  const { data: itemsSistema } = useConfigByGrupo("sistema");
  const updateMut = useUpdateConfig();
  const [values, setValues] = useState<Record<string, string>>({});
  const [tz, setTzValue] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (itemsSistema) {
      const v = itemsSistema.find((i: ConfigItem) => i.key === K_TZ)?.value;
      setTzValue(typeof v === "string" ? v : "America/Mexico_City");
    }
  }, [itemsSistema]);

  useEffect(() => {
    if (items) {
      const initial: Record<string, string> = {};
      items.forEach((it: ConfigItem) => {
        initial[it.key] = typeof it.value === "string" ? it.value : "";
      });
      setValues(initial);
      setDirty(false);
    }
  }, [items]);

  function updateField(key: string, val: string) {
    setValues((v) => ({ ...v, [key]: val }));
    setDirty(true);
  }

  async function handleSave() {
    const tzVal = tz.trim();
    if (!tzVal) {
      toast.error("Zona horaria vacía", { description: "Ej. America/Mexico_City" });
      return;
    }
    try {
      // rc130 — zona horaria (grupo sistema); el backend rechaza nombres inválidos
      await updateMut.mutateAsync({ key: K_TZ, value: tzVal, value_type: "string", grupo: "sistema" });
      setTz(tzVal);
      // Guardar cada campo que haya cambiado (simple: todos)
      for (const f of FIELDS) {
        await updateMut.mutateAsync({
          key: f.key,
          value: values[f.key] ?? "",
          value_type: "string",
          grupo: "isp",
        });
      }
      toast.success("Datos del ISP guardados");
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", {
        description: (e as ApiError).message,
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        Estos datos se usan para generar contratos, facturas y otros documentos.
        Asegúrate que el RFC y la razón social sean exactamente como aparecen en tu
        constancia fiscal.
      </p>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.multiline ? "lg:col-span-2" : ""}>
            <Label>{f.label}</Label>
            {f.multiline ? (
              <Textarea
                rows={2}
                value={values[f.key] ?? ""}
                onChange={(e) => updateField(f.key, e.target.value)}
                placeholder={f.placeholder}
              />
            ) : (
              <Input
                value={values[f.key] ?? ""}
                onChange={(e) => updateField(f.key, e.target.value)}
                placeholder={f.placeholder}
              />
            )}
          </div>
        ))}
      </div>

      {/* rc130 — zona horaria del ISP: manda sobre fechas de corte, facturación,
          "hoy" de cobranza, documentos y lo que muestra el panel. */}
      <div className="rounded-lg border bg-card p-4 space-y-2">
        <Label>Zona horaria</Label>
        <Input
          list="tz-sugeridas"
          value={tz}
          onChange={(e) => { setTzValue(e.target.value); setDirty(true); }}
          placeholder="America/Mexico_City"
          className="font-mono"
        />
        <datalist id="tz-sugeridas">
          {TZ_SUGERIDAS.map((z) => <option key={z} value={z} />)}
        </datalist>
        <p className="text-[11px] text-muted-foreground">
          Nombre IANA (ej. <span className="font-mono">America/Monterrey</span>). Define el "hoy"
          de facturación y cobranza, la hora de los pollers y cómo se muestran las fechas en el
          panel. Los pollers y el otro proceso del API la toman en menos de 5 minutos.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs text-muted-foreground">Cambios sin guardar</span>
        )}
        <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
          <Save size={12} />
          {updateMut.isPending ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}
